// Der Dorfgenerator: erzeugt aus einem festen Bauplan immer dasselbe Dorf, ohne Zufall.
// Dorfplatz mit Brunnen in der Mitte, ein Weg in Nord-Süd-Richtung, Fachwerkhäuser beidseitig,
// Marktstand und Pfosten am Platz, Bäume, im Südosten eine eingezäunte Wiese mit Stall und Tor,
// rundherum eine Hecke als sichtbare Weltgrenze. Reine Daten, kein DOM, kein Rendering.
//
// Ab Paket 2 wird derselbe Bauplan zweimal gebraucht: `generateVillage()` liefert weiter das Dorf
// der Demo (4 × 4 Chunks, mit Hecke, Wiese, Stall und Kuh), und `baueDorf()` setzt denselben
// Bauplan mit einem Versatz in eine größere Welt – dort ohne Wiese, Zaun, Stall und Kuh, weil die
// Tiere auf den Parzellen stehen (docs/architektur-paket-2.md, „Welt mit Parzellen“, ADR 0023).

import { BLOCK } from './blocks.js';
import { CHUNK_SIZE, createWorld, setBlock, worldBounds, type World } from './chunk.js';
import {
  PREFABS,
  PREFAB_LEGEND,
  drawFence,
  findPrefabCells,
  rotatePrefab,
  stampPrefab,
  type PrefabName,
} from './prefabs.js';
import type { AnimalSpawn, Landmark, Pen, SpawnPoint, Village } from './types.js';

/** Kennung der Welt, die der Server im `hello` mitschickt. */
export const WORLD_ID = 'dorf-v1';

/** Der Boden: Stein bis y = 5, Erde auf y = 6, die Oberfläche (Gras, Pflaster, Böden) auf y = 7. */
export const BODEN = { steinBis: 5, erde: 6, oberflaeche: 7 } as const;
/** Fußhöhe: Avatar, Tiere und Landmarks stehen auf der Oberfläche, also auf y = 8. */
export const FUSS_Y = BODEN.oberflaeche + 1;

/** Ein Rechteck in Blöcken, alle Grenzen einschließlich. */
export interface Rechteck {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

interface HausPlan {
  prefab: PrefabName;
  /** Ursprung des Bauteils (West-Nord-Ecke). */
  x: number;
  z: number;
  /** Vierteldrehungen im Uhrzeigersinn; die Häuser sind mit Tür nach Osten gezeichnet. */
  drehung: number;
}

/** Der Nord-Süd-Weg des Dorfes in Dorfkoordinaten; er wird in der großen Welt verlängert. */
const WEG_X = { min: 29, max: 33 } as const;
/**
 * Ein Streifen quer über den Dorfplatz, in dem kein Haus und kein Bauteil steht. Die große Welt
 * legt darauf den Ost-West-Weg; im Dorf der Demo ist er einfach Teil des Platzes.
 */
const QUERWEG_Z = { min: 34, max: 38 } as const;

/**
 * Was der Weltgenerator (`world.ts`) vom Dorfbauplan wissen muss: Größe, Bodenaufbau und die
 * beiden Streifen, auf denen die Wege das Dorf verlassen.
 */
export const DORF = {
  /** Kantenlänge des Dorfes in Chunks. */
  chunks: 4,
  /** Kantenlänge des Dorfes in Blöcken. */
  bloecke: 4 * CHUNK_SIZE,
  boden: BODEN,
  fussY: FUSS_Y,
  /** x-Streifen des Nord-Süd-Weges, in Dorfkoordinaten. */
  wegX: WEG_X,
  /** z-Streifen des Ost-West-Weges, in Dorfkoordinaten. */
  querWegZ: QUERWEG_Z,
} as const;

/**
 * Der Bauplan: feste Zahlen, nichts wird zur Laufzeit gewürfelt. Koordinaten sind Blöcke,
 * x nach Osten, z nach Süden; die Welt ist 4 × 4 Chunks, also 0..63 in beiden Richtungen.
 */
const BAUPLAN = {
  chunksX: DORF.chunks,
  chunksZ: DORF.chunks,
  /** Hecke am Rand: laub auf y = 8 und 9. */
  heckeHoehe: 2,
  /** Weg in Nord-Süd-Richtung, von Hecke zu Hecke. */
  weg: { minX: WEG_X.min, minZ: 1, maxX: WEG_X.max, maxZ: 62 } satisfies Rechteck,
  /** Der gepflasterte Dorfplatz in der Mitte. */
  platz: { minX: 21, minZ: 24, maxX: 41, maxZ: 38 } satisfies Rechteck,
  /** Ursprung des Brunnens (3 × 3), der Wasserblock liegt bei (31, 31). */
  brunnen: { x: 30, z: 30 },
  /** Startzelle des Avatars, direkt östlich neben dem Brunnenring. */
  spawn: { x: 33, z: 31 },
  /** Marktstand nordöstlich des Brunnens, Theke nach Süden. */
  marktstand: { x: 35, z: 25 },
  /** Ein Pfosten südwestlich des Brunnens. */
  pfosten: { x: 26, z: 35 },
  haeuser: [
    { prefab: 'fachwerkhaus_gross', x: 20, z: 10, drehung: 0 },
    { prefab: 'fachwerkhaus_klein', x: 36, z: 12, drehung: 2 },
    { prefab: 'fachwerkhaus_klein', x: 16, z: 29, drehung: 0 },
    { prefab: 'fachwerkhaus_klein', x: 42, z: 29, drehung: 2 },
    { prefab: 'fachwerkhaus_klein', x: 22, z: 46, drehung: 0 },
    { prefab: 'fachwerkhaus_gross', x: 36, z: 44, drehung: 2 },
  ] satisfies HausPlan[],
  /** Stammpositionen der Bäume. */
  baeume: [
    { x: 8, z: 8 },
    { x: 8, z: 24 },
    { x: 8, z: 44 },
    { x: 14, z: 56 },
    { x: 52, z: 8 },
    { x: 56, z: 24 },
    { x: 50, z: 38 },
    { x: 24, z: 58 },
    { x: 38, z: 58 },
  ],
  koppel: {
    id: 'koppel',
    /** Die Zaunlinie selbst; die Koppel ist die Fläche innerhalb. */
    zaun: { minX: 44, minZ: 44, maxX: 60, maxZ: 60 } satisfies Rechteck,
    /** Die Torlücke auf der Westseite, dem Weg zugewandt. */
    tor: { x: 44, z: 52 },
    /** Ursprung des Stalls (5 × 4), offene Front nach Westen. */
    stall: { x: 54, z: 46 },
    kuh: { id: 'kuh-1', species: 'kuh' },
  },
} as const;

/**
 * Eine Welt samt Versatz: Alle Bauplan-Koordinaten sind Dorfkoordinaten und werden erst hier auf
 * Weltkoordinaten geschoben. Im Dorf der Demo ist der Versatz null.
 */
interface Bauplatz {
  world: World;
  dx: number;
  dz: number;
}

/** Was `baueDorf` an Daten zurückgibt; die Blöcke stehen dann schon in der Welt. */
export interface DorfErgebnis {
  spawn: SpawnPoint;
  pens: Pen[];
  animals: AnimalSpawn[];
  landmarks: Landmark[];
}

/** Erzeugt das Dorf. Jeder Aufruf liefert eine neue, aber Byte für Byte gleiche Welt. */
export function generateVillage(): Village {
  const world = createWorld(BAUPLAN.chunksX, BAUPLAN.chunksZ);
  fuelleBoden(world);
  pflanzeHecke(world, BAUPLAN.heckeHoehe);
  const dorf = baueDorf(world, 0, 0, { mitKoppel: true });
  return { world, ...dorf, worldId: WORLD_ID };
}

/**
 * Setzt den Dorfbauplan mit dem Versatz (dx, dz) in eine bestehende Welt. Boden und Hecke gehören
 * der Welt, nicht dem Dorf, und werden vom Aufrufer gelegt. Ohne `mitKoppel` bleiben Wiese, Zaun,
 * Stall und Kuh weg – in der großen Welt stehen die Tiere auf den Parzellen.
 */
export function baueDorf(
  world: World,
  dx: number,
  dz: number,
  optionen: { mitKoppel: boolean },
): DorfErgebnis {
  const platz: Bauplatz = { world, dx, dz };
  const landmarks: Landmark[] = [];

  fuelleRechteck(platz, BAUPLAN.weg, BODEN.oberflaeche, BLOCK.pflaster);
  fuelleRechteck(platz, BAUPLAN.platz, BODEN.oberflaeche, BLOCK.pflaster);

  for (const plan of BAUPLAN.haeuser) landmarks.push(baueHaus(platz, plan));

  const brunnen = PREFABS.brunnen;
  stampe(platz, brunnen, BAUPLAN.brunnen.x, BODEN.oberflaeche, BAUPLAN.brunnen.z);
  const [wasser] = findPrefabCells(brunnen, 'W');
  if (wasser === undefined) throw new Error('Bauteil brunnen hat keinen Wasserblock');
  landmarks.push(marke(platz, 'brunnen', BAUPLAN.brunnen.x + wasser.x, BAUPLAN.brunnen.z + wasser.z));

  stampe(platz, PREFABS.marktstand, BAUPLAN.marktstand.x, FUSS_Y, BAUPLAN.marktstand.z);
  // Mitte unter dem Dach: Ursprung plus (1, 1) beim 3 × 3 großen Stand.
  landmarks.push(marke(platz, 'marktstand', BAUPLAN.marktstand.x + 1, BAUPLAN.marktstand.z + 1));

  stampe(platz, PREFABS.pfosten, BAUPLAN.pfosten.x, BODEN.oberflaeche, BAUPLAN.pfosten.z);
  landmarks.push(marke(platz, 'pfosten', BAUPLAN.pfosten.x, BAUPLAN.pfosten.z));

  for (const stamm of BAUPLAN.baeume) {
    const baum = PREFABS.baum;
    const halbX = (baum.size[0] - 1) / 2;
    const halbZ = (baum.size[2] - 1) / 2;
    stampe(platz, baum, stamm.x - halbX, FUSS_Y, stamm.z - halbZ);
    landmarks.push(marke(platz, 'baum', stamm.x, stamm.z));
  }

  const pens: Pen[] = [];
  const animals: AnimalSpawn[] = [];
  // Blickziel des Avatars: die Wiese, wenn es sie gibt, sonst der Brunnen in der Mitte.
  let blickziel = { x: BAUPLAN.brunnen.x + 1.5, z: BAUPLAN.brunnen.z + 1.5 };

  if (optionen.mitKoppel) {
    const pen = baueKoppel(platz, landmarks);
    const kuhMitte = penMitte(pen);
    pens.push(pen);
    animals.push({
      id: BAUPLAN.koppel.kuh.id,
      species: BAUPLAN.koppel.kuh.species,
      x: kuhMitte.x,
      y: pen.y,
      z: kuhMitte.z,
      penId: pen.id,
    });
    blickziel = { x: kuhMitte.x - dx, z: kuhMitte.z - dz };
  }

  // Startpunkt in der Mitte der Startzelle, Blick auf das Blickziel (beides in Dorfkoordinaten).
  const spawnX = BAUPLAN.spawn.x + 0.5;
  const spawnZ = BAUPLAN.spawn.z + 0.5;
  const spawn: SpawnPoint = {
    x: dx + spawnX,
    y: FUSS_Y,
    z: dz + spawnZ,
    yaw: yawZu(spawnX, spawnZ, blickziel.x, blickziel.z),
  };

  return { spawn, pens, animals, landmarks };
}

/** Stein bis y = 5, Erde auf y = 6, Gras auf y = 7, in jeder Säule der Welt. */
export function fuelleBoden(world: World): void {
  jedeSaeule(world, (x, z) => {
    for (let y = 0; y <= BODEN.steinBis; y++) setBlock(world, x, y, z, BLOCK.stein);
    setBlock(world, x, BODEN.erde, z, BLOCK.erde);
    setBlock(world, x, BODEN.oberflaeche, z, BLOCK.gras);
  });
}

/** Eine dichte Hecke aus laub auf dem äußersten Ring der Welt, `hoehe` Blöcke hoch. */
export function pflanzeHecke(world: World, hoehe: number = BAUPLAN.heckeHoehe): void {
  const b = worldBounds(world);
  jedeSaeule(world, (x, z) => {
    const rand = x === b.minX || x === b.maxX - 1 || z === b.minZ || z === b.maxZ - 1;
    if (!rand) return;
    for (let i = 0; i < hoehe; i++) setBlock(world, x, FUSS_Y + i, z, BLOCK.laub);
  });
}

/** Setzt ein Haus ab, legt einen Pflasterpfad von der Tür zum Weg und liefert das Landmark. */
function baueHaus(platz: Bauplatz, plan: HausPlan): Landmark {
  const haus = rotatePrefab(PREFABS[plan.prefab], plan.drehung);
  stampe(platz, haus, plan.x, BODEN.oberflaeche, plan.z);
  const [tuer] = findPrefabCells(haus, 'T');
  if (tuer === undefined) throw new Error(`Bauteil ${plan.prefab} hat keine Tür`);
  const tuerX = plan.x + tuer.x;
  const tuerZ = plan.z + tuer.z;
  // Trampelpfad von der Tür zum Weg, auf Höhe der Tür; über den Platz hinweg ist er wirkungslos.
  const weg = BAUPLAN.weg;
  if (tuerX < weg.minX) {
    fuelleRechteck(platz, { minX: tuerX + 1, maxX: weg.minX - 1, minZ: tuerZ, maxZ: tuerZ }, BODEN.oberflaeche, BLOCK.pflaster);
  } else if (tuerX > weg.maxX) {
    fuelleRechteck(platz, { minX: weg.maxX + 1, maxX: tuerX - 1, minZ: tuerZ, maxZ: tuerZ }, BODEN.oberflaeche, BLOCK.pflaster);
  }
  return marke(platz, 'haus', tuerX, tuerZ);
}

/** Zaun mit Tor, Pfad vom Weg zum Tor, Stall in der Wiese; liefert die Koppel in Weltkoordinaten. */
function baueKoppel(platz: Bauplatz, landmarks: Landmark[]): Pen {
  const { zaun, tor, stall, id } = BAUPLAN.koppel;
  zaunLinie(platz, zaun.minX, zaun.minZ, zaun.maxX, zaun.minZ);
  zaunLinie(platz, zaun.minX, zaun.maxZ, zaun.maxX, zaun.maxZ);
  zaunLinie(platz, zaun.maxX, zaun.minZ, zaun.maxX, zaun.maxZ);
  zaunLinie(platz, zaun.minX, zaun.minZ, zaun.minX, zaun.maxZ, tor);
  // Pflasterpfad vom Weg bis in die Torlücke.
  fuelleRechteck(platz, { minX: BAUPLAN.weg.maxX + 1, maxX: tor.x, minZ: tor.z, maxZ: tor.z }, BODEN.oberflaeche, BLOCK.pflaster);

  stampe(platz, PREFABS.stall, stall.x, FUSS_Y, stall.z);
  // Mitte des Innenraums: beim 5 × 4 großen Stall Ursprung plus (2, 1).
  landmarks.push(marke(platz, 'stall', stall.x + 2, stall.z + 1));
  landmarks.push(marke(platz, 'tor', tor.x, tor.z));

  return {
    id,
    minX: platz.dx + zaun.minX + 1,
    minZ: platz.dz + zaun.minZ + 1,
    maxX: platz.dx + zaun.maxX - 1,
    maxZ: platz.dz + zaun.maxZ - 1,
    y: FUSS_Y,
  };
}

/** Mittelpunkt der Koppel als Fußpunkt (Blockmitte). */
function penMitte(pen: Pen): { x: number; z: number } {
  return { x: (pen.minX + pen.maxX + 1) / 2, z: (pen.minZ + pen.maxZ + 1) / 2 };
}

/**
 * Blickwinkel von (fromX, fromZ) nach (toX, toZ). Der Blickvektor zu `yaw` ist
 * (-sin yaw, -cos yaw): yaw = 0 blickt nach -z (Norden), positive Werte drehen nach links.
 * Aus -sin yaw = dx und -cos yaw = dz folgt yaw = atan2(-dx, -dz).
 */
export function yawZu(fromX: number, fromZ: number, toX: number, toZ: number): number {
  return Math.atan2(-(toX - fromX), -(toZ - fromZ));
}

/** Ein Landmark aus Dorfkoordinaten; y ist immer die Fußhöhe. */
function marke(platz: Bauplatz, kind: Landmark['kind'], x: number, z: number): Landmark {
  return { kind, x: platz.dx + x, y: FUSS_Y, z: platz.dz + z };
}

function stampe(platz: Bauplatz, prefab: (typeof PREFABS)[PrefabName], x: number, y: number, z: number): void {
  stampPrefab(platz.world, prefab, PREFAB_LEGEND, platz.dx + x, y, platz.dz + z);
}

function zaunLinie(platz: Bauplatz, x1: number, z1: number, x2: number, z2: number, luecke?: { x: number; z: number }): void {
  const versetzt = luecke === undefined ? undefined : { x: platz.dx + luecke.x, z: platz.dz + luecke.z };
  drawFence(platz.world, platz.dx + x1, platz.dz + z1, platz.dx + x2, platz.dz + z2, FUSS_Y, versetzt);
}

function fuelleRechteck(platz: Bauplatz, r: Rechteck, y: number, id: number): void {
  for (let z = r.minZ; z <= r.maxZ; z++) {
    for (let x = r.minX; x <= r.maxX; x++) setBlock(platz.world, platz.dx + x, y, platz.dz + z, id);
  }
}

/** Ruft `f` für jede Säule (x, z) der Welt auf. */
function jedeSaeule(world: World, f: (x: number, z: number) => void): void {
  const b = worldBounds(world);
  for (let z = b.minZ; z < b.maxZ; z++) {
    for (let x = b.minX; x < b.maxX; x++) f(x, z);
  }
}
